import type {
  CreateSubjectBody,
  SubjectListEntry,
  SubjectListLevel,
  SubjectListResponse,
  SubjectListSort,
  SubjectResearchStatus,
  SubjectStatus,
} from '@ni/shared';
import type { SubjectListParams } from '../api/client';

/**
 * Defaults are omitted from parameters so clearing filters returns to the same
 * cached list. Each changed query key starts paging from its own first page.
 */
export interface PeopleChoices {
  /** The search text, as it stands after the debounce. */
  q: string;
  /** The chosen status words — none chosen means every status. */
  statuses: readonly string[];
  level: SubjectListLevel;
  sort: SubjectListSort;
  researchStatus: SubjectResearchStatus | '';
  /**
   * **One import session's own people** (import note §2.6) — `?import=<id>` on
   * the address, which is where the link at the end of a commit lands.
   *
   * `null` is the ordinary list. It has no default to fall back to and is
   * therefore always sent when it is set, unlike the defaulted controls above.
   */
  importId: string | null;
  /**
   * **One protection account's own people** (security fold, 2026-09-22) —
   * `?account_id=<id>` on the address, which is where the account page's roster
   * link and the record's own account chip land.
   *
   * `null` is every account, which is the whole list in a security org and the
   * only reading anywhere else: a vertical with no accounts refuses the
   * parameter, so it is never sent there.
   */
  accountId: string | null;
}

/** The screen as it opens: everyone the org tracks, most recently touched first. */
export const PEOPLE_DEFAULTS: PeopleChoices = {
  q: '',
  statuses: [],
  level: 'tracked',
  sort: 'updated',
  researchStatus: '',
  importId: null,
  accountId: null,
};

/**
 * The choices as parameters. Anything at its default — and an empty search — is
 * left off, so the shortest list of what was actually asked for is what the key
 * carries and what the server receives.
 */
export function peopleParams(choices: PeopleChoices): SubjectListParams {
  const params: SubjectListParams = {};
  const q = choices.q.trim();
  if (q.length > 0) params.q = q;
  if (choices.researchStatus) params.research_status = choices.researchStatus;
  if (choices.statuses.length > 0) params.status = [...choices.statuses];
  if (choices.level !== PEOPLE_DEFAULTS.level) params.level = choices.level;
  if (choices.sort !== PEOPLE_DEFAULTS.sort) params.sort = choices.sort;
  if (choices.importId !== null) params.import_id = choices.importId;
  if (choices.accountId !== null) params.account_id = choices.accountId;
  return params;
}

/**
 * The pages of a paged read as one list. Load more appends, so the rows already
 * read stay where they are and the next page arrives under them — the reader
 * never loses their place to a re-sort they did not ask for.
 */
export function flattenPages(
  pages: readonly SubjectListResponse[] | undefined,
): SubjectListEntry[] {
  return pages ? pages.flatMap((page) => page.entries) : [];
}

/**
 * **The adopt door, as a create** (import note §1.3): the known person's own
 * entity, given the chosen word.
 *
 * `entity_id` is the whole gesture — it is what makes this a promotion rather
 * than a second person under the same name, and everything already on the
 * entity comes with it.
 *
 * **And no prose.** The route wants a sentence about why a person is being
 * entered, and this gesture has none: the office pressed Track on a row in a
 * list, and everything known about that person is already on the entity, which
 * is the point of adopting it. Sending a line to satisfy the field — "Tracked
 * from the people list." — filed a sentence nobody wrote as the office's own
 * intake prose and spent an extraction reading it for facts it did not contain.
 * The field is optional when `entity_id` is given (ruled 2026-09-06), so it is
 * left out.
 *
 * It takes a name and an entity rather than a whole `SubjectListEntry` because
 * the same door is now on two lists: the People screen's rows and the knowledge
 * base's, whose rows spell the name `display_name`. Those two fields are the
 * whole of the gesture, and a signature that says so is a signature neither
 * caller has to reshape a row for.
 *
 * **And the row's own protection account**, where it has one (security fold,
 * 2026-09-22). Adopting a known person in a security org files the record under
 * the account the entity already belongs to — the row's, never the list's
 * chip: the entity is on one account by construction, and reading the account
 * off the filter would refile it whenever the list happened to be unfiltered.
 * `null` in every other vertical is the key left off, which is what the create
 * door requires there.
 */
export function promoteBody(
  entry: { name: string; entity_id: string; account?: { id: string } | null },
  status: SubjectStatus,
): CreateSubjectBody {
  return {
    name: entry.name,
    status,
    entity_id: entry.entity_id,
    ...(entry.account ? { account_id: entry.account.id } : {}),
  };
}
